import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/context/AuthContext";
import { useCampaign } from "@/context/CampaignContext";
import { useSearch } from "@/context/SearchContext";
import gmailIcon from "../assets/images/gmail.webp";
import microsoftIcon from "../assets/images/outlook.webp";
import yahooIcon from "../assets/images/yahoopng.png";
import zohoIcon from "../assets/images/zohomail.png";
import smtp from "../assets/images/smtp.svg";
import EmailConnectModal from "@/components/campaigns/EmailConnectModal";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Mail,
  User,
  Building2,
  MapPin,
  Briefcase,
  FileText,
  Shield,
  Plus,
  MailCheck,
  PlusCircle,
} from "lucide-react";
import { CampaignProvider } from "@/context/CampaignContext";
import {
  checkSpam,
  createNewEmailTemplate,
  fetchAllEmailTemplates,
  saveCustomEmailTemplate,
} from "../Redux/ReduxSlices/EmailTemplateSlice";
import { useDispatch, useSelector } from "react-redux";
import { createCampaignAPi } from "../Redux/ReduxSlices/campaignSlice";
import { fetchUserDetails } from "../Redux/ReduxSlices/profileSlice";
import { toast } from "sonner";

// Mock email templates
const emailTemplates = [
  {
    id: "1",
    name: "Introduction",
    content:
      "Hi {{name}},\n\nI noticed your work at {{company}} and thought I'd reach out. Our solution helps companies like yours to improve their sales process.\n\nWould you be open to a 15-minute call next week?\n\nBest regards,\n{{user_name}}",
  },
  {
    id: "2",
    name: "Follow-up",
    content:
      "Hi {{name}},\n\nI wanted to follow up on my previous email. I'd love to show you how our platform has helped similar companies in the {{industry}} industry.\n\nAre you available for a quick chat this week?\n\nBest regards,\n{{user_name}}",
  },
  {
    id: "3",
    name: "Product Announcement",
    content:
      "Hi {{name}},\n\nI'm excited to share that we've just launched a new feature that addresses the exact challenges that {{company}} might be facing with lead generation.\n\nI'd be happy to give you a personalized demo.\n\nBest regards,\n{{user_name}}",
  },
];

export default function CampaignNewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    // createCampaign,
    // isEmailConnected,
    isLoading,
    selectedRecipients,
    clearSelectedRecipients,
  } = useCampaign();
  const [selectedProvider, setSelectedProvider] = useState("");
  const { templates, error } = useSelector((state) => state.customEmail);
  const { details: userData } = useSelector((state) => state.profile);
  const isEmailConnected = userData?.data.is_email_connected;

  const providerSummary = userData?.provider_summary;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // safely get only connected providers
  const connectedProviders = providerSummary
    ? Object.entries(providerSummary).filter(
        ([key, provider]) => provider && provider.connected,
      )
    : [];

  useEffect(() => {
    dispatch(fetchAllEmailTemplates());
    dispatch(fetchUserDetails());
  }, [dispatch]);

  const [showEmailConnect, setShowEmailConnect] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [selectedTab, setSelectedTab] = useState("template");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [spamScore, setSpamScore] = useState(null);
  const [checkingSpam, setCheckingSpam] = useState(false);
  const [subjectData, setSubjectData] = useState("");
  const [bodyData, setBodyData] = useState("");
  const [footerData, setFooterData] = useState("");
  // New template modal states
  const [showNewTemplateModal, setShowNewTemplateModal] = useState(false);
  // Save template confirmation modal state
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);

  // React Hook Form for new template
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      name: "",
      designation: "",
      product_name: "",
      features: "",
      tone: "professional",
      goal: "start a conversation",
      from_name: "",
      company: "",
      industry: "",
      personalization: "",
    },
  });

  const watchedTone = watch("tone");
  const watchedGoal = watch("goal");
  const {
    spam_score,
    loading,
    reason,
    improved_email,
    provider_impact,
    inbox_readiness,
    spam_analysis,
  } = useSelector((state) => state.customEmail);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  // Initialize selected recipients from context
  useEffect(() => {
    setSelectedPeople(selectedRecipients.people || []);
    setSelectedCompanies(selectedRecipients.companies || []);
  }, [selectedRecipients]);

  const totalRecipients =
    selectedPeople.filter((person) => person && person.id).length +
    selectedCompanies.filter((company) => company && company.id).length;

  const handleCreateCampaign = async () => {
    if (!campaignName.trim()) {
      toast.warning("Please enter a campaign name");
      return;
    }

    if (totalRecipients === 0) {
      toast.warning("Please select at least one recipient");
      return;
    }

    if (!isEmailConnected) {
      setShowEmailConnect(true);
      return;
    }

    const recipientIds = [
      ...selectedPeople.map((p) => p.id),
      ...selectedCompanies.map((c) => c.id),
    ];

    const emailContent =
      selectedTab === "template" && selectedTemplateId
        ? emailTemplates.find((t) => t.id === selectedTemplateId)?.content
        : `${subjectData}\n\n${bodyData}\n\n${footerData}`;

    const payload = {
      campaign_name: campaignName,
      scheduled_at: new Date().toISOString(),
      // email_subject: subjectData || "Quick launch?",
      // email_content: emailContent,
      is_custom_email: selectedTab === "custom",
      recipient_ids: recipientIds,
      from_provider: selectedProvider,
      // from_provider: "outlook",
      template_id: selectedTab === "template" ? selectedTemplateId : null,
    };

    try {
      const result = await dispatch(createCampaignAPi(payload)).unwrap();

      toast.success("Campaign created successfully:");
      setTimeout(() => {
        navigate("/campaigns");
      }, 3000);
    } catch (error) {
      console.error("Failed to create campaign:", error);

      toast.error("error", error?.message || "Error creating campaign");
    }
  };

  const handleRemovePerson = (personId) => {
    setSelectedPeople((prev) => prev.filter((p) => p.id !== personId));
  };

  const handleRemoveCompany = (companyId) => {
    setSelectedCompanies((prev) => prev.filter((c) => c.id !== companyId));
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      setCustomMessage(
        `${template.subject_data || ""}\n\n${template.body_data || ""}\n\n${
          template.footer_data || ""
        }`,
      );
      setSubjectData(template.subject_data || "");
      setBodyData(template.body_data || "");
      setFooterData(template.footer_data || "");
    }
  };

  const handleCheckSpamScore = () => {
    if (!(subjectData.trim() || bodyData.trim() || footerData.trim())) return;

    // Build preview text
    const previewText = `
    Subject: ${subjectData || ""}
    
    ${bodyData || ""}
    
    ${footerData || ""}
  `.trim();

    const payload = {
      email_text: previewText,
      use_llm: false, // or true if you want LLM check
    };

    dispatch(checkSpam(payload));
  };

  const onSubmitNewTemplate = async (data) => {
    const payload = {
      name: data.name,
      designation: data.designation,
      product_name: data.product_name,
      features: data.features,
      tone: data.tone,
      goal: data.goal,
      from_name: data.from_name,
      company: data.company,
      industry: data.industry,
      personalization: data.personalization,
    };

    // return
    try {
      await dispatch(createNewEmailTemplate(payload)).unwrap();
      setShowNewTemplateModal(false);
      reset(); // Reset form using React Hook Form
      // You might want to refresh templates list here
    } catch (error) {
      console.error("Error creating template:", error);
      toast.success("Login successful!");
    }
  };

  const handleCloseModal = () => {
    setShowNewTemplateModal(false);
    reset(); // Reset form when closing modal
  };

  const handleSaveTemplate = () => {
    setShowSaveConfirmModal(true);
  };

  const confirmSaveTemplate = () => {
    const payload = {
      subject_data: subjectData,
      body_data: bodyData,
      footer_data: footerData,
      spam_score: spam_score || 0,
    };
    dispatch(saveCustomEmailTemplate(payload)).then(() => {
      // Refresh templates list
      dispatch(fetchAllEmailTemplates()).then(() => {
        // Switch to template tab
        setSelectedTab("template");
        // Clear the form fields
        setSubjectData("");
        setBodyData("");
        setFooterData("");
        setShowSaveConfirmModal(false);
      });
    });
  };

  if (!user) {
    return null;
  }

  const handleProviderChange = (providerName) => {
    setSelectedProvider(providerName);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1 bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-6 text-left">
            <h1 className="text-2xl font-semibold text-black mb-2">
              <PlusCircle className="h-4 w-4 mr-2" />
              Create New Campaign
            </h1>
            <p className="text-gray-600 max-w-2xl">
              Set up your email outreach campaign with selected leads and start
              building meaningful connections.
            </p>
          </div>

          {/* Email Connection Warning - Moved to top */}
          {!isEmailConnected ? (
            // If no provider connected -> show "Connect Now"
            <Card className="border border-gray-200 bg-gray-50 mb-8">
              <CardContent className="pt-6 pb-6">
                <div className="text-center">
                  <h3 className="text-base font-semibold text-black mb-2">
                    Connect Your Email
                  </h3>
                  <p className="text-sm text-black mb-4 max-w-sm mx-auto">
                    Connect your email to send campaigns
                  </p>
                  <Button
                    onClick={() => setShowEmailConnect(true)}
                    className="bg-black hover:bg-gray-800 text-white font-medium h-9"
                  >
                    Connect Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            // If providers are connected -> show list of connected ones
            <Card className="border border-green-200 bg-green-50 mb-8">
              <CardContent className="pt-6 pb-6">
                <h3 className="text-base font-semibold text-green-900 mb-4">
                  Connected Email Providers
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {connectedProviders.map(([providerName, provider]) => (
                    <div
                      key={providerName}
                      className="flex items-center bg-white shadow-sm rounded-lg p-4 border"
                    >
                      <input
                        type="radio"
                        name="provider"
                        value={providerName}
                        checked={selectedProvider === providerName}
                        onChange={() => handleProviderChange(providerName)}
                        className="mr-3 h-4 w-4 "
                      />

                      <div className="flex items-center gap-3">
                        {providerName.toLowerCase() === "gmail" && (
                          <img
                            src={gmailIcon}
                            alt="Gmail"
                            className="h-6 w-6"
                          />
                        )}
                        {providerName.toLowerCase() === "outlook" && (
                          <img
                            src={microsoftIcon}
                            alt="Outlook"
                            className="h-6 w-6"
                          />
                        )}
                        {providerName.toLowerCase() === "yahoo" && (
                          <img
                            src={yahooIcon}
                            alt="Yahoo"
                            className="h-6 w-6"
                          />
                        )}
                        {providerName.toLowerCase() === "zoho" && (
                          <img src={zohoIcon} alt="Zoho" className="h-6 w-6" />
                        )}

                        <div>
                          <span className="font-medium text-gray-800 capitalize block">
                            {providerName}
                          </span>
                          <span className="text-sm text-gray-600">
                            {provider.from_email}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 ml-auto">
                        {provider.status === "expired" ? (
                          <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-600">
                            Expired
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-600">
                            Active
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Debug */}
                <div className="mt-4 text-sm text-gray-700">
                  Selected: {selectedProvider || "None"}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Campaign Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Campaign Details */}
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-6">
                  <CardTitle className="text-lg font-semibold text-black">
                    Campaign Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="campaign-name"
                      className="text-sm font-semibold text-black"
                    >
                      Campaign Name
                    </Label>
                    <div className="relative">
                      <Input
                        id="campaign-name"
                        type="text"
                        placeholder="e.g., Q1 Product Launch Outreach"
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                        className="h-10 pl-4 pr-4 border border-gray-300 focus:border-black focus:ring-1 focus:ring-black rounded-lg text-black placeholder-gray-400"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email Template */}
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-6">
                  <CardTitle className="text-lg font-semibold text-black">
                    Email Content
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Tabs
                    value={selectedTab}
                    onValueChange={setSelectedTab}
                    className="w-full"
                  >
                    <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg">
                      <TabsTrigger
                        value="template"
                        className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-black font-medium"
                      >
                        Use Template
                      </TabsTrigger>
                      <TabsTrigger
                        value="custom"
                        className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-black font-medium"
                      >
                        Custom Message
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="template" className="space-y-4 mt-4">
                      {/* Create New Template Button */}
                      <div className="flex justify-end mb-4">
                        <Button
                          onClick={() => setShowNewTemplateModal(true)}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2 border-gray-300 hover:border-black"
                        >
                          <Plus className="h-4 w-4" />
                          Create New Template
                        </Button>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-semibold text-black">
                          Select Template
                        </Label>
                        <Select
                          value={selectedTemplateId}
                          onValueChange={handleTemplateSelect}
                        >
                          <SelectTrigger className="h-10 border border-gray-300 focus:border-black rounded-lg">
                            <SelectValue placeholder="Choose a template to get started" />
                          </SelectTrigger>
                          <SelectContent className="rounded-lg border border-gray-200">
                            <SelectGroup>
                              {templates.length > 0 ? (
                                templates.map((template) => (
                                  <SelectItem
                                    key={template.id}
                                    value={template.id}
                                    className="rounded-lg hover:bg-gray-100 cursor-pointer px-3 py-2"
                                  >
                                    <div className="flex flex-col space-y-1">
                                      <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                                        Subject
                                      </span>
                                      <span className="text-sm font-semibold text-gray-800">
                                        {template.subject_data || "No subject"}
                                      </span>
                                    </div>
                                  </SelectItem>
                                ))
                              ) : (
                                <p className="text-sm text-gray-500 p-2">
                                  No templates found
                                </p>
                              )}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>

                      {selectedTemplateId && (
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-black">
                            Preview
                          </Label>
                          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                            <div className="bg-white rounded-lg p-3 border border-gray-100">
                              <div className="text-sm text-black whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">
                                {customMessage}
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-gray-500">
                              Variables will be automatically replaced
                            </div>
                          </div>
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="custom" className="space-y-4 mt-4">
                      <div className="space-y-4">
                        {/* Subject */}
                        <div className="space-y-2">
                          <Label className="text-xs font-semibold text-black">
                            Subject
                          </Label>
                          <Input
                            type="text"
                            placeholder="Write your subject line here..."
                            value={subjectData}
                            onChange={(e) => setSubjectData(e.target.value)}
                            className="border border-gray-300 focus:border-black focus:ring-1 focus:ring-black rounded-lg text-black placeholder-gray-400"
                          />
                          <div className="text-xs text-gray-400">
                            {subjectData.length} chars
                          </div>
                        </div>

                        {/* Body */}
                        <div className="space-y-2">
                          <Label className="text-xs font-semibold text-black">
                            Body
                          </Label>
                          <div className="relative">
                            <Textarea
                              placeholder="Write your personalized email body here...\n\nTip: Use {{name}}, {{company}}, {{title}}"
                              value={bodyData}
                              onChange={(e) => setBodyData(e.target.value)}
                              className="min-h-[160px] border border-gray-300 focus:border-black focus:ring-1 focus:ring-black rounded-lg text-black placeholder-gray-400 resize-none"
                            />
                            <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                              {bodyData.length} chars
                            </div>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="space-y-2">
                          <Label className="text-xs font-semibold text-black">
                            Footer
                          </Label>
                          <Textarea
                            placeholder="Example: You received this email because you signed up..."
                            value={footerData}
                            onChange={(e) => setFooterData(e.target.value)}
                            className="min-h-[80px] border border-gray-300 focus:border-black focus:ring-1 focus:ring-black rounded-lg text-black placeholder-gray-400 resize-none"
                          />
                          <div className="text-xs text-gray-400">
                            {footerData.length} chars
                          </div>
                        </div>

                        {/* Spam Score Button */}
                        {(subjectData.trim() ||
                          bodyData.trim() ||
                          footerData.trim()) && (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleCheckSpamScore}
                              disabled={
                                checkingSpam ||
                                !(
                                  subjectData.trim() ||
                                  bodyData.trim() ||
                                  footerData.trim()
                                )
                              }
                              className="mb-3 border-2 border-orange-300 hover:border-orange-600 hover:bg-orange-50 rounded-full transition-all duration-200 shadow-sm hover:shadow-lg"
                            >
                              {checkingSpam ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Shield className="h-4 w-4 mr-2" />
                              )}
                              {checkingSpam
                                ? "Checking..."
                                : "Check Spam Score"}
                            </Button>

                            {/* Spam Score Result */}
                            {spam_score !== null && (
                              <div
                                className={`mb-3 p-3 rounded-lg border ${
                                  spam_score <= 3
                                    ? "bg-green-50 border-green-200"
                                    : spam_score <= 6
                                      ? "bg-yellow-50 border-yellow-200"
                                      : "bg-red-50 border-red-200"
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <Shield
                                    className={`h-4 w-4 ${
                                      spam_score <= 3
                                        ? "text-green-600"
                                        : spam_score <= 6
                                          ? "text-yellow-600"
                                          : "text-red-600"
                                    }`}
                                  />
                                  <span className="text-sm font-medium">
                                    Spam Score: {spam_score}/10
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600">
                                  {spam_analysis.reason}
                                </p>
                              </div>
                            )}

                            {/* Preview */}
                            <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                              <div className="mb-2">
                                <span className="text-xs font-medium text-black">
                                  Preview
                                </span>
                              </div>
                              <div className="bg-white rounded-lg p-2 text-xs text-black whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto border border-gray-100">
                                <strong>Subject:</strong>{" "}
                                {subjectData || "(empty)"}
                                {"\n\n"}
                                {bodyData || "(empty)"}
                                {"\n\n"}
                                <em>{footerData || "(empty)"}</em>
                              </div>
                            </div>
                          </>
                        )}

                        <div className="flex justify-end">
                          <Button
                            type="button"
                            onClick={handleSaveTemplate}
                            disabled={loading} // <-- save action
                            className="bg-black hover:bg-gray-800 text-white font-medium h-9"
                          >
                            {loading ? "Saving..." : "Save Template"}
                          </Button>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>

            {/* Recipients Sidebar */}
            <div className="lg:col-span-1">
              <Card className="border border-gray-200 bg-white sticky top-4">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-black flex items-center justify-between">
                    <span>Recipients</span>
                    <Badge
                      variant="secondary"
                      className="bg-gray-100 text-black border-gray-200 text-xs"
                    >
                      {totalRecipients}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {totalRecipients === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <User className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-base font-medium text-black mb-2">
                        No recipients yet
                      </h3>
                      <p className="text-gray-500 mb-4 text-sm">
                        Start by finding and selecting leads
                      </p>
                      <Button
                        onClick={() => navigate("/search")}
                        className="w-full bg-black hover:bg-gray-800 text-white h-9"
                      >
                        Find Leads
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Selected People */}
                      {selectedPeople.length > 0 && (
                        <div>
                          <h4 className="font-medium text-xs text-black mb-2 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            People ({selectedPeople.length})
                          </h4>
                          <div className="space-y-2">
                            {selectedPeople
                              .filter((person) => person && person.id)
                              .map((person) => (
                                <div
                                  key={person.id}
                                  className="group flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-all duration-200"
                                >
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                      <User className="h-4 w-4 text-gray-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-xs text-black truncate">
                                        {person.name || "Unknown Person"}
                                      </p>
                                      <p className="text-xs text-gray-600 truncate">
                                        {person.title || "Unknown Title"}
                                      </p>
                                      <p className="text-xs text-gray-500 truncate">
                                        {person.company || "Unknown Company"}
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleRemovePerson(person.id)
                                    }
                                    className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-black hover:bg-gray-200 transition-all duration-200 h-6 w-6 p-0"
                                  >
                                    ×
                                  </Button>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      <Button
                        variant="outline"
                        className="w-full h-8 border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-all duration-200 text-sm"
                        onClick={() => navigate("/search")}
                      >
                        <User className="mr-2 h-3 w-3" />
                        Add More Recipients
                      </Button>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="pt-4">
                  <Button
                    className="w-full h-10 bg-black hover:bg-gray-800 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleCreateCampaign}
                    disabled={
                      isLoading || totalRecipients === 0 || !campaignName.trim()
                    }
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Campaign"
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <EmailConnectModal
        open={showEmailConnect}
        onClose={() => setShowEmailConnect(false)}
      />

      {/* New Template Creation Modal */}
      <Dialog open={showNewTemplateModal} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Template</DialogTitle>
            <DialogDescription>
              Fill in the details below to create a new email template.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmitNewTemplate)}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Template Name *</Label>
                  <Input
                    id="name"
                    {...register("name", {
                      required: "Template name is required",
                      minLength: {
                        value: 2,
                        message: "Template name must be at least 2 characters",
                      },
                    })}
                    placeholder="Enter template name"
                    className={errors.name ? "border-red-500" : ""}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="designation">Designation</Label>
                  <Input
                    id="designation"
                    {...register("designation")}
                    placeholder="Enter designation"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="product_name">Product Name</Label>
                  <Input
                    id="product_name"
                    {...register("product_name")}
                    placeholder="Enter product name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="from_name">From Name</Label>
                  <Input
                    id="from_name"
                    {...register("from_name")}
                    placeholder="Enter sender name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    {...register("company")}
                    placeholder="Enter company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    {...register("industry")}
                    placeholder="Enter industry"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tone">Tone</Label>
                  <Select
                    value={watchedTone}
                    onValueChange={(value) => setValue("tone", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="formal">Formal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="goal">Goal</Label>
                  <Select
                    value={watchedGoal}
                    onValueChange={(value) => setValue("goal", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select goal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="start a conversation">
                        Start a conversation
                      </SelectItem>
                      <SelectItem value="schedule a meeting">
                        Schedule a meeting
                      </SelectItem>
                      <SelectItem value="product demo">Product demo</SelectItem>
                      <SelectItem value="follow up">Follow up</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="features">Features</Label>
                <Textarea
                  id="features"
                  {...register("features")}
                  placeholder="Describe key features or benefits"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="personalization">Personalization</Label>
                <Textarea
                  id="personalization"
                  {...register("personalization")}
                  placeholder="Add personalization details"
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Template"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Save Template Confirmation Modal */}
      <Dialog
        open={showSaveConfirmModal}
        onOpenChange={setShowSaveConfirmModal}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Email Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to save this template? This will create a
              new template that you can reuse in future campaigns.
              <p>
                Please take a moment to review the email content carefully, as
                it will be sent in this campaign
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSaveConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmSaveTemplate}
              disabled={loading}
              className="bg-black hover:bg-gray-800 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Template"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CampaignNewPageWrapper() {
  return (
    <CampaignProvider>
      <CampaignNewPage />
    </CampaignProvider>
  );
}
